import { useState, type ReactNode } from 'react';
import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  usePatientReferralLetters,
  useReferral,
  type PatientReferralLetter,
} from '@/hooks/useReferrals';
import { ReferralLetterDetails } from '@/components/referrals/ReferralLetterDetails';
import { ReferralOverviewCard } from '@/components/referrals/ReferralOverviewCard';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import { canRevealReferralCpr } from '@/lib/referral/referralCprReveal';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { PatientTabFrame } from '@/components/patient/PatientTabFrame';

interface PatientReferralLetterTabProps {
  patientId: string;
  action?: ReactNode;
}

function referralOptionLabel(referral: PatientReferralLetter): string {
  const date = new Date(referral.received_at).toLocaleDateString('da-DK');
  const from =
    referral.referrer_name ||
    [referral.sender_hospital, referral.sender_department].filter(Boolean).join(', ');
  return from ? `${date} · ${from}` : date;
}

/**
 * Patient page "Henvisning" tab, Modtagne: the letter details inside the list,
 * and the same henvisningsoplysninger card as /henvisninger/:id underneath.
 * Newest first; a selector switches when there are several.
 */
export function PatientReferralLetterTab({ patientId, action }: PatientReferralLetterTabProps) {
  const { t } = useTranslation();
  const { data: referrals, isLoading, error, refetch } = usePatientReferralLetters(patientId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const rows = referrals ?? [];
  const empty = !isLoading && !error && rows.length === 0;
  const selected = rows.find((r) => r.id === selectedId) ?? rows[0];
  const overview = useReferral(selected?.id);
  const { hasPermission } = useAuth();
  const { activeClinicId } = useActiveClinic();
  const canRevealCpr = canRevealReferralCpr(
    (clinicId) => hasPermission(clinicId, 'patients_cpr', 'view'),
    activeClinicId,
    overview.data?.clinic_id,
  );

  return (
    <>
      <PatientTabFrame
        title={t('patients.tabs.henvisning')}
        count={rows.length}
        action={action}
        loading={isLoading}
        error={error ? t('patients.henvisning_load_error') : undefined}
        onRetry={() => void refetch()}
        empty={
          empty
            ? {
                icon: <FileText className="h-7 w-7" />,
                title: t('patients.henvisning_none'),
                description: t('patients.henvisning_empty_hint'),
              }
            : undefined
        }
      >
        {selected ? (
          <div className="mt-s4 space-y-4">
            {rows.length > 1 ? (
              <div className="max-w-md">
                <label
                  htmlFor="patient-referral-letter-select"
                  className={cn(TYPE.label, 'block text-ds-muted')}
                >
                  {t('patients.henvisning_select', { count: rows.length })}
                </label>
                <select
                  id="patient-referral-letter-select"
                  value={selected.id}
                  onChange={(event) => setSelectedId(event.target.value)}
                  className="mt-1 block w-full rounded-lg border border-ds-field px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-1 focus:ring-ds-teal"
                >
                  {rows.map((referral) => (
                    <option key={referral.id} value={referral.id}>
                      {referralOptionLabel(referral)}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            <ReferralLetterDetails referral={selected} />
          </div>
        ) : null}
      </PatientTabFrame>
      {overview.data ? (
        <ReferralOverviewCard referral={overview.data} canRevealCpr={canRevealCpr} />
      ) : null}
    </>
  );
}
